import userQueries from '@apis/queries/userQueries';
import {
    LOCATION_CACHE_MS, locationAgreedAtom, locationFetchedAtAtom, userLatLonAtom,
} from '@atoms/locationAtoms';
import useToast from '@hooks/ui/useToast';
import useNativeListener from '@hooks/useNativeListener';
import { useQuery } from '@tanstack/react-query';
import {
    checkPermission, getLocation, Permission, requestPermission,
} from '@utils/nativeUtils';
import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ILocation {
    lat: number,
    lon: number,
}

function useLocation() {
    const { t } = useTranslation('shopList');
    const toast = useToast();
    const [locationAgreed, setLocationAgreed] = useAtom(locationAgreedAtom);
    const [location, setLocation] = useAtom(userLatLonAtom);
    const [fetchedAt, setFetchedAt] = useAtom(locationFetchedAtAtom);
    const [ready, setReady] = useState<boolean>(false);
    const { data: user } = useQuery(userQueries.detail());
    // user 객체가 재조회로 바뀌어도 위치를 다시 요청하지 않도록 필요한 값만 의존성으로 쓴다
    const hasUser = !!user;
    const userLocationAgreed = !!user?.locationAgreed;
    // 이 화면에서 이미 위치를 요청했는지
    const requestedRef = useRef<boolean>(false);

    useNativeListener('getLocation', (result: ILocation) => {
        console.log('getLocation', { result });
        if (result && (user?.locationAgreed || !user)) {
            setLocation(result);
            setFetchedAt(Date.now());
        }
        setLocationAgreed(!!result && (!!user?.locationAgreed || !user));
        setReady(true);
    }, [user]);

    useNativeListener('checkPermission', (permission: Permission, hasPermission: boolean) => {
        console.log('checkPermission', { permission, hasPermission });
        if (user?.locationAgreed || !user) {
            if (permission === Permission.LOCATION && hasPermission) {
                getLocation();
            } else if (permission === Permission.LOCATION) {
                requestPermission(Permission.LOCATION);
            }
        }
    }, [user]);

    useNativeListener('requestPermission', (permission: Permission, result: boolean) => {
        console.log('requestPermission', { permission, result });
        if (permission === Permission.LOCATION) {
            setLocationAgreed(result);
            if (result) {
                toast.success(t('toast.locationAllowed'));
            }
            getLocation();
        }
    });

    useEffect(() => {
        const allowed = !hasUser || userLocationAgreed;
        if (!allowed) {
            setLocationAgreed(false);
            setReady(true);
            return;
        }
        if (requestedRef.current) {
            return;
        }
        requestedRef.current = true;
        // 메인에서 미리 받아둔 위치가 유효하면 권한 확인 없이 바로 사용
        const hasCachedLocation = location.lat !== 0
            && Date.now() - fetchedAt < LOCATION_CACHE_MS;
        if (hasCachedLocation) {
            setLocationAgreed(true);
            setReady(true);
        } else {
            checkPermission(Permission.LOCATION);
        }
    }, [hasUser, userLocationAgreed]);

    return {
        locationAgreed, ready,
    };
}

export default useLocation;
